import React, { useState } from 'react';
import {
  Feedback,
  FormField,
  Icon,
  Input,
  Toggle,
} from '@fashionnovateam/nova-ui-toolkit';
import { includes, values } from 'lodash';
import { formatPrice } from '../../PurchaseOrderDetail/Utils/formatPrice';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  UpdatePurchaseOrderRevisionForm,
} from './validation';
import { PurchaseOrderLine } from '../../../Lib/Types/purchase-order';
import { CostId } from './formTargetId';
import { ErrorWithIcon } from './ErrorWithIcon';

type PricesFormProps = {
  jrCost?: number;
  plusCost?: number;
  item: NonNullable<PurchaseOrderRevisionForm['items']>[0];
  index: number;
  lines: PurchaseOrderLine[];
  styleColor: string;
  name: string;
  updateForm: UpdatePurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

type PurchaseOrderRevisionPrices = NonNullable<
  NonNullable<PurchaseOrderRevisionForm['items']>[number]['prices']
>;

export function PricesForm(props: PricesFormProps) {
  const {
    jrCost,
    plusCost,
    styleColor,
    item,
    index,
    errors,
    disabled,
    updateForm,
  } = props;

  const [formPrevValue, setFormPrevValue] =
    useState<PurchaseOrderRevisionPrices>({
      junior: undefined,
      plus: undefined,
    });

  const getPrices = (
    checked: boolean,
    prices: PurchaseOrderRevisionPrices | undefined,
  ) => {
    if (checked) {
      return formPrevValue;
    }
    if (prices !== undefined) {
      setFormPrevValue(prices);
    }
    return undefined;
  };
  return (
    <div className="flex flex-col gap-5" id={`${CostId}-${index}`}>
      <div className="flex items-center gap-3">
        <Toggle
          onChange={(e) => {
            updateForm((prev) => ({
              ...prev,
              items: prev.items?.map((i) => {
                if (i.style_color !== styleColor) return i;
                return {
                  ...i,
                  prices: getPrices(e.target.checked, i.prices),
                };
              }),
            }));
          }}
          checked={!!item.prices}
          disabled={disabled}
        />
        <span className="text-sm font-medium text-base-primary">
          Cost Change
        </span>
      </div>
      {item.prices && (
        <>
          {jrCost && (
            <div className="flex flex-col gap-2">
              <FormField label="JR Cost">
                <Input
                  type="number"
                  value={item.prices.junior ?? ''}
                  onChange={(e) => {
                    updateForm((prev) => ({
                      ...prev,
                      items: prev.items?.map((prevItem) => {
                        if (prevItem.style_color !== styleColor)
                          return prevItem;
                        if (!e.target.value) {
                          return {
                            ...prevItem,
                            prices: { ...prevItem.prices, junior: undefined },
                          };
                        }
                        return {
                          ...prevItem,
                          prices: {
                            ...prevItem.prices,
                            junior: parseFloat(e.target.value) || 0,
                          },
                        };
                      }),
                    }));
                  }}
                  onWheel={(e) => {
                    e.currentTarget.blur();
                  }}
                  error={!!errors?.[`items.${index}.prices.junior`]}
                  disabled={disabled}
                />
              </FormField>
              <div className="flex items-center gap-2">
                <Icon
                  name="alert/alert-circle"
                  className="text-primary-700 text-md"
                />
                <span className="text-xsm font-medium text-base-primary">{`Currently ${formatPrice(jrCost, false, false)}`}</span>
              </div>
            </div>
          )}
          {plusCost && (
            <div className="flex flex-col gap-2">
              <FormField label="Plus Cost">
                <Input
                  type="number"
                  value={item.prices.plus ?? ''}
                  onChange={(e) => {
                    updateForm((prev) => ({
                      ...prev,
                      items: prev.items?.map((prevItem) => {
                        if (prevItem.style_color !== styleColor)
                          return prevItem;
                        if (!e.target.value) {
                          return {
                            ...prevItem,
                            prices: { ...prevItem.prices, plus: undefined },
                          };
                        }
                        return {
                          ...prevItem,
                          prices: {
                            ...prevItem.prices,
                            plus: parseFloat(e.target.value) || 0,
                          },
                        };
                      }),
                    }));
                  }}
                  onWheel={(e) => {
                    e.currentTarget.blur();
                  }}
                  error={!!errors?.[`items.${index}.prices.plus`]}
                  disabled={disabled}
                />
              </FormField>
              <div className="flex items-center gap-2">
                <Icon
                  name="alert/alert-circle"
                  className="text-primary-700 text-md"
                />
                <span className="text-xsm font-medium text-base-primary">{`Currently ${formatPrice(plusCost, false, false)}`}</span>
              </div>
            </div>
          )}
          {errors?.[`items.${index}.prices`] && (
            <ErrorWithIcon error={errors?.[`items.${index}.prices`]} />
          )}
          {includes(values(errors), 'negative_cost') && (
            <Feedback
              type="error"
              title="You cannot enter a negative or $0.00 cost."
            />
          )}
        </>
      )}
    </div>
  );
}
